import { useState, useEffect, useCallback } from "react";

import adminFetch from "../../utils/adminFetch";

const API = `${process.env.REACT_APP_API_URL}`;

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}
function shortId(id) { return id ? id.slice(0, 8).toUpperCase() : "—"; }

// ── Badge ─────────────────────────────────────────────────────────────────────
function Badge({ type }) {
  const styles = {
    voice: { background: "#ede9fe", color: "#6d28d9" },
    text:  { background: "#dbeafe", color: "#1d4ed8" },
  };
  return (
    <span style={{ ...styles[type] || styles.text, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      {type === "voice" ? "🎙 Voice" : "💬 Text"}
    </span>
  );
}

// ── Conversation row ──────────────────────────────────────────────────────────
function ConversationRow({ conv, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const firstUser = conv.messages.find((m) => m.role === "user");
  const hasVoice  = conv.messages.some((m) => m.inputType === "voice");
  const msgCount  = conv.messages.length;

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", marginBottom: 10 }}>
      <div
        onClick={() => setExpanded((e) => !e)}
        style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, background: "#fff", transition: "background .15s" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
      >
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#002db5,#0040e6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>Session {shortId(conv.sessionId)}</span>
            <Badge type={hasVoice ? "voice" : "text"} />
            <span style={{ fontSize: 11, color: "#94a3b8" }}>{msgCount} message{msgCount !== 1 ? "s" : ""}</span>
          </div>
          <div style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {firstUser?.content || "No messages"}
          </div>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: "#64748b" }}>{fmt(conv.updatedAt)}</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{conv.ipAddress || "—"}</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(conv._id); }}
            title="Delete"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /></svg>
          </button>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid #f1f5f9", background: "#fafafa", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {conv.messages.map((msg, i) => {
            const isUser = msg.role === "user";
            return (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", flexDirection: isUser ? "row-reverse" : "row" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: isUser ? "#e2e8f0" : "linear-gradient(135deg,#002db5,#0040e6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {isUser
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="13" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="19" x2="12" y2="22" /><line x1="8" y1="22" x2="16" y2="22" /></svg>
                  }
                </div>
                <div style={{ maxWidth: "78%" }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 3, textAlign: isUser ? "right" : "left" }}>
                    {isUser ? "User" : "Paladin AI"} · {fmt(msg.timestamp)}
                    {msg.inputType === "voice" && <span style={{ marginLeft: 5, color: "#6d28d9" }}>🎙</span>}
                  </div>
                  <div style={{ padding: "8px 12px", borderRadius: isUser ? "12px 12px 4px 12px" : "12px 12px 12px 4px", background: isUser ? "#1e3a8a" : "#fff", color: isUser ? "#fff" : "#1e293b", fontSize: 13, lineHeight: 1.55, border: isUser ? "none" : "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
                    {msg.content}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Analytics tab ─────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = "#002db5" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", flex: "1 1 140px" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function HourlyChart({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  const labels = ["12a","1a","2a","3a","4a","5a","6a","7a","8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p","11p"];
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Conversations by Hour of Day</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80 }}>
        {data.map((d, i) => (
          <div key={i} title={`${labels[i]}: ${d.count} conversation${d.count !== 1 ? "s" : ""}`}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", cursor: "default" }}>
            <div style={{
              width: "100%",
              background: d.count > 0 ? "linear-gradient(180deg,#002db5,#0040e6)" : "#e2e8f0",
              borderRadius: "3px 3px 0 0",
              height: `${Math.max(4, (d.count / max) * 72)}px`,
              transition: "height .4s, background .2s",
              opacity: d.count > 0 ? 1 : 0.35,
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTop: "1px solid #f1f5f9" }}>
        {[0, 6, 12, 18, 23].map((h) => (
          <span key={h} style={{ fontSize: 10, color: "#94a3b8" }}>{labels[h]}</span>
        ))}
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${API}/api/admin/voice-analytics`);
      const d = await res.json();
      if (d.success) setData(d.data);
      else setError(d.message);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#64748b" }}>Loading analytics…</div>;
  if (error)   return <div style={{ textAlign: "center", padding: 40, color: "#ef4444" }}>{error}</div>;
  if (!data)   return null;

  const voicePct = data.totalMessages > 0 ? Math.round((data.voiceMessages / data.totalMessages) * 100) : 0;
  const textPct  = 100 - voicePct;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Stat cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatCard label="Total Conversations" value={data.totalConversations} />
        <StatCard label="Last 24 Hours"        value={data.last24h}           color="#0891b2" />
        <StatCard label="Last 7 Days"          value={data.last7d}            color="#7c3aed" />
        <StatCard label="Total Messages"       value={data.totalMessages}     color="#0f172a" />
        <StatCard label="Avg / Session"        value={data.avgMessagesPerSession} sub="messages per conversation" color="#059669" />
      </div>

      {/* Voice vs Text bar */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Voice vs Text Breakdown</div>
        <div style={{ display: "flex", gap: 24, marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#6d28d9" }} />
            <span style={{ fontSize: 13, color: "#0f172a" }}>Voice — {data.voiceMessages} ({voicePct}%)</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#1d4ed8" }} />
            <span style={{ fontSize: 13, color: "#0f172a" }}>Text — {data.textMessages} ({textPct}%)</span>
          </div>
        </div>
        {data.totalMessages > 0 ? (
          <div style={{ height: 10, borderRadius: 6, background: "#dbeafe", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${voicePct}%`, background: "linear-gradient(90deg,#7c3aed,#4f46e5)", borderRadius: 6, transition: "width .5s" }} />
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#94a3b8" }}>No messages recorded yet.</div>
        )}
      </div>

      {/* Hourly chart */}
      {data.hourlyBreakdown && <HourlyChart data={data.hourlyBreakdown} />}

      <div style={{ textAlign: "right" }}>
        <button onClick={load} disabled={loading}
          style={{ padding: "6px 14px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, cursor: "pointer", color: "#475569", fontFamily: "inherit" }}>
          ↻ Refresh
        </button>
      </div>
    </div>
  );
}

// ── System Prompt tab ─────────────────────────────────────────────────────────
function SystemPromptTab() {
  const [content, setContent]           = useState("");
  const [defaultContent, setDefault]    = useState("");
  const [isCustom, setIsCustom]         = useState(false);
  const [updatedAt, setUpdatedAt]       = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [status, setStatus]             = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API}/api/admin/system-prompt`);
      const d = await res.json();
      if (d.success) {
        setContent(d.content);
        setDefault(d.defaultContent);
        setIsCustom(d.isCustom);
        setUpdatedAt(d.updatedAt);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await adminFetch(`${API}/api/admin/system-prompt`, {
        method: "PUT",
        body: JSON.stringify({ content }),
      });
      const d = await res.json();
      if (d.success) {
        setIsCustom(true);
        setUpdatedAt(new Date().toISOString());
        setStatus({ type: "success", msg: "Prompt saved — AI will use the updated version on the next conversation." });
      } else {
        setStatus({ type: "error", msg: d.message });
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.message });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the built-in default prompt? Your custom version will be deleted.")) return;
    try {
      const res = await adminFetch(`${API}/api/admin/system-prompt`, {
        method: "DELETE",
      });
      const d = await res.json();
      if (d.success) {
        setContent(defaultContent);
        setIsCustom(false);
        setUpdatedAt(null);
        setStatus({ type: "success", msg: "Reset to built-in default prompt." });
      } else {
        setStatus({ type: "error", msg: d.message });
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.message });
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "#64748b" }}>Loading prompt…</div>;

  return (
    <div style={{ maxWidth: 820 }}>
      {/* Status banner */}
      <div style={{
        background: isCustom ? "#f0fdf4" : "#f0f9ff",
        border: `1px solid ${isCustom ? "#86efac" : "#bae6fd"}`,
        borderRadius: 10, padding: "10px 14px", marginBottom: 14,
        fontSize: 12, color: isCustom ? "#166534" : "#0369a1",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: isCustom ? "#22c55e" : "#0284c7", flexShrink: 0 }} />
        {isCustom
          ? `Using custom prompt · Last saved ${fmt(updatedAt)}`
          : "Using built-in default prompt — no custom version saved yet."
        }
      </div>

      <textarea
        value={content}
        onChange={(e) => { setContent(e.target.value); setStatus(null); }}
        rows={22}
        style={{
          width: "100%", padding: "14px 16px", borderRadius: 12,
          border: "1px solid #e2e8f0", fontSize: 13, lineHeight: 1.65,
          fontFamily: "ui-monospace,Menlo,monospace", resize: "vertical",
          outline: "none", boxSizing: "border-box", color: "#1e293b",
          transition: "border-color .15s",
        }}
        onFocus={(e) => (e.target.style.borderColor = "#002db5")}
        onBlur={(e)  => (e.target.style.borderColor = "#e2e8f0")}
      />
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, textAlign: "right" }}>
        {content.length.toLocaleString()} characters
      </div>

      {status && (
        <div style={{
          padding: "10px 14px", borderRadius: 10, marginTop: 10,
          background: status.type === "success" ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${status.type === "success" ? "#86efac" : "#fca5a5"}`,
          fontSize: 13, color: status.type === "success" ? "#166534" : "#dc2626",
        }}>
          {status.msg}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button
          onClick={save}
          disabled={saving}
          style={{
            padding: "10px 24px", background: "linear-gradient(135deg,#012E72,#002db5)",
            color: "#fff", border: "none", borderRadius: 10, fontSize: 13,
            fontWeight: 600, cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1, fontFamily: "inherit",
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {isCustom && (
          <button
            onClick={reset}
            style={{
              padding: "10px 20px", background: "#fff", color: "#ef4444",
              border: "1px solid #fca5a5", borderRadius: 10, fontSize: 13,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Reset to Default
          </button>
        )}
      </div>

      <div style={{ marginTop: 14, padding: "10px 14px", background: "#fefce8", border: "1px solid #fde047", borderRadius: 10, fontSize: 12, color: "#854d0e" }}>
        ⚡ Changes take effect immediately — no server restart needed. The AI uses your updated prompt on the very next conversation.
      </div>
    </div>
  );
}

// ── API Config tab ────────────────────────────────────────────────────────────
function ApiConfigTab() {
  const [config, setConfig]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    adminFetch(`${API}/api/admin/voice-config`)
      .then((r) => r.json())
      .then((d) => { if (d.success) setConfig(d.config); })
      .finally(() => setLoading(false));
  }, []);

  const reveal = async (keyId) => {
    if (revealed[keyId]) { setRevealed((r) => ({ ...r, [keyId]: null })); return; }
    const res = await adminFetch(`${API}/api/admin/voice-config/reveal/${keyId}`);
    const d = await res.json();
    if (d.success) setRevealed((r) => ({ ...r, [keyId]: d.value }));
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Loading config…</div>;
  if (!config) return <div style={{ padding: 40, textAlign: "center", color: "#ef4444" }}>Failed to load config.</div>;

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#0284c7", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>AI Provider</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{config.provider}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#0284c7", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>Chat Model</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{config.chatModel}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#0284c7", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>STT Model</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{config.sttModel}</div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {config.keys.map((k) => (
          <div key={k.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: k.configured ? "#22c55e" : "#ef4444", flexShrink: 0 }} title={k.configured ? "Configured" : "Not configured"} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>{k.label}</div>
              <code style={{ fontSize: 12, color: "#64748b", fontFamily: "monospace", wordBreak: "break-all" }}>
                {k.configured
                  ? (revealed[k.id] || k.masked || "••••••••••••••••")
                  : <span style={{ color: "#ef4444" }}>Not configured</span>
                }
              </code>
            </div>
            {k.configured && (
              <button
                onClick={() => reveal(k.id)}
                title={revealed[k.id] ? "Hide" : "Reveal"}
                style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "5px 10px", cursor: "pointer", color: "#64748b", fontSize: 12, display: "flex", alignItems: "center", gap: 5, flexShrink: 0, transition: "background .15s", fontFamily: "inherit" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
              >
                {revealed[k.id]
                  ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg> Hide</>
                  : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> Reveal</>
                }
              </button>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16, padding: "10px 14px", background: "#fefce8", border: "1px solid #fde047", borderRadius: 10, fontSize: 12, color: "#854d0e" }}>
        ⚠ To update keys or switch between Groq and OpenAI, edit <code>server/.env</code> and restart the server.
      </div>
    </div>
  );
}

// ── Conversations tab ─────────────────────────────────────────────────────────
function ConversationsTab() {
  const [conversations, setConversations] = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [pages, setPages]   = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery]   = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p = 1, q = query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20, ...(q ? { search: q } : {}) });
      const res = await adminFetch(`${API}/api/admin/conversations?${params}`);
      const d = await res.json();
      if (d.success) { setConversations(d.data); setTotal(d.total); setPage(d.page); setPages(d.pages); }
    } finally { setLoading(false); }
  }, [query]);

  useEffect(() => { load(1, query); }, [query]);

  useEffect(() => {
    const interval = setInterval(() => load(page, query), 30000);
    return () => clearInterval(interval);
  }, [page, query, load]);

  const handleSearch = (e) => { e.preventDefault(); setQuery(search); };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    await adminFetch(`${API}/api/admin/conversations/${id}`, { method: "DELETE" });
    load(page);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <form onSubmit={handleSearch} style={{ display: "flex", gap: 8, flex: 1 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations…"
            style={{ flex: 1, padding: "8px 14px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 13, outline: "none", fontFamily: "inherit" }}
          />
          <button type="submit" style={{ padding: "8px 16px", background: "#002db5", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Search</button>
          {query && <button type="button" onClick={() => { setSearch(""); setQuery(""); }} style={{ padding: "8px 12px", background: "#f1f5f9", border: "none", borderRadius: 10, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>}
        </form>
        <button onClick={() => load(page)} disabled={loading} title="Refresh"
          style={{ padding: "8px 12px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#475569", flexShrink: 0, fontFamily: "inherit" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ animation: loading ? "va-spin .8s linear infinite" : "none" }}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Refresh
        </button>
        <div style={{ fontSize: 13, color: "#64748b", flexShrink: 0 }}>{total} conversation{total !== 1 ? "s" : ""}</div>
      </div>

      {loading
        ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Loading…</div>
        : conversations.length === 0
          ? (
            <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: .5 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              <div style={{ fontSize: 14 }}>No conversations yet</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>They'll appear here once users chat with the assistant</div>
            </div>
          )
          : conversations.map((c) => <ConversationRow key={c._id} conv={c} onDelete={handleDelete} />)
      }

      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => load(page - 1)} style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 8, background: page <= 1 ? "#f8fafc" : "#fff", cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "inherit" }}>← Prev</button>
          <span style={{ padding: "6px 14px", fontSize: 13, color: "#64748b" }}>Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => load(page + 1)} style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 8, background: page >= pages ? "#f8fafc" : "#fff", cursor: page >= pages ? "not-allowed" : "pointer", fontSize: 13, fontFamily: "inherit" }}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminVoiceAgent() {
  const [tab, setTab] = useState("analytics");

  const tabs = [
    { id: "analytics",      label: "Analytics" },
    { id: "conversations",  label: "Conversations" },
    { id: "system-prompt",  label: "AI Prompt" },
    { id: "config",         label: "API Keys" },
  ];

  return (
    <div style={{ padding: "28px 32px", fontFamily: "system-ui,-apple-system,sans-serif", maxWidth: 1000 }}>
      <style>{`@keyframes va-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0 }}>Voice Agent Management</h1>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Analytics, conversations, AI prompt, and API configuration.</p>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #f1f5f9", marginBottom: 24 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
              fontSize: 14, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? "#002db5" : "#64748b",
              borderBottom: tab === t.id ? "2px solid #002db5" : "2px solid transparent",
              marginBottom: -2, transition: "color .15s", fontFamily: "inherit",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "analytics"     && <AnalyticsTab />}
      {tab === "conversations" && <ConversationsTab />}
      {tab === "system-prompt" && <SystemPromptTab />}
      {tab === "config"        && <ApiConfigTab />}
    </div>
  );
}
